import { Helmet } from 'react-helmet-async';
import { openingHoursSchema } from '../config/business';

const SITE_NAME = 'SeknuTo.cz';
const SITE_URL = 'https://seknuto.cz';
const SITE_PHONE = '+420730588372';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

const DEFAULT_SEO = {
  title: 'Sekání trávy Dvůr Králové | Zahradnické služby | SeknuTo.cz',
  description: 'Profesionální sekání trávy, likvidace zarostlých pozemků a údržba zahrad ve Dvoře Králové nad Labem a okolí. Bezplatná obhlídka a kalkulace na míru. 730 588 372',
  keywords: 'sekání trávy Dvůr Králové, zahradnické služby, likvidace pozemků, čištění zarostlých parcel, údržba zahrad, vertikutace trávníku, hnojení trávníku, sekání trávy Trutnov, sekání trávy Vrchlabí, zahradník Dvůr Králové',
  canonical: SITE_URL,
  image: DEFAULT_IMAGE,
};

// og:image a JSON-LD potřebují absolutní URL; data: URI (base64 z databáze)
// do hlavičky nepatří – nahradí se výchozím obrázkem.
export const absoluteImage = (src) => {
  if (!src || typeof src !== 'string' || src.startsWith('data:')) return DEFAULT_IMAGE;
  return src.startsWith('/') ? `${SITE_URL}${src}` : src;
};

export default function SEOHead({
  title = DEFAULT_SEO.title,
  description = DEFAULT_SEO.description,
  keywords = DEFAULT_SEO.keywords,
  canonical = DEFAULT_SEO.canonical,
  image = DEFAULT_SEO.image,
  schema = null,
  noindex = false,
}) {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  image = absoluteImage(image);

  return (
    <Helmet>
      <html lang="cs" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="cs_CZ" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      <meta name="format-detection" content="telephone=yes" />

      {schema && (
        Array.isArray(schema)
          ? schema.map((s, i) => (
              <script key={i} type="application/ld+json">
                {JSON.stringify(s)}
              </script>
            ))
          : <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
    </Helmet>
  );
}

// Obsluhovaná města – jedna definice pro LocalBusiness i jednotlivé služby
const SERVED_CITIES = [
  'Dvůr Králové nad Labem', 'Hradec Králové', 'Trutnov', 'Vrchlabí', 'Hostinné',
  'Jaroměř', 'Náchod', 'Hořice', 'Jičín', 'Česká Skalice', 'Nová Paka', 'Úpice', 'Hronov', 'Smiřice',
];
const servedArea = () => [
  ...SERVED_CITIES.map((name) => ({ '@type': 'City', name })),
  // Dojezd 50 km od sídla – pro AI odpovědi typu „zahradník do 50 km od …“
  {
    '@type': 'GeoCircle',
    geoMidpoint: { '@type': 'GeoCoordinates', latitude: 50.4318, longitude: 15.8142 },
    geoRadius: 50000,
  },
];

// Všechny služby s odkazem na vlastní stránku – AI asistenti i Google tak
// vidí úplnou nabídku a vědí, kam odkázat. Žádné ceny (jen po obhlídce).
const SERVICES = [
  ['Sekání trávy', 'Pravidelné i jednorázové sekání trávníků s vyžnutím okrajů, odvozem nebo mulčováním', '/pravidelne-sekani-travy'],
  ['Sekání přerostlé trávy', 'Hrubé sekání vysoké trávy a buřeně křovinořezem a mulčovačem', '/sekani-prerostle-travy'],
  ['Stříhání keřů, tújí a živých plotů', 'Stříhání a tvarování keřů, tújí a živých plotů, ořez a prořez stromů', '/strihani-keru-kaceni-stromu'],
  ['Kácení stromů', 'Kácení stromů včetně rizikového kácení po částech, štěpkování a odvozu dřeva', '/kaceni-stromu'],
  ['Likvidace a čištění zarostlých pozemků', 'Vyčištění zarostlých pozemků od křovin, náletů a vysoké trávy, mulčování a odvoz', '/likvidace-pozemku'],
  ['Čištění a úklid zahrady', 'Jarní a podzimní úklid zahrady, hrabání listí, čištění záhonů a odplevelení', '/cisteni-zahrady'],
  ['Realizace zahrad', 'Realizace zahrad na klíč – návrh, terénní úpravy, výsadba a zakládání trávníků', '/realizace-zahrad'],
  ['Pokládání trávníku', 'Pokládka travního koberce i zakládání trávníku ze semene včetně přípravy podloží', '/pokladani-travniku'],
  ['Vertikutace trávníku', 'Vertikutace, provzdušnění, hnojení a obnova trávníku', '/vertikutace-travniku'],
  ['Údržba zahrady', 'Pravidelná péče o zahradu, sezónní balíčky a celoroční VIP servis', '/udrzba-zahrady'],
  ['Odvoz bioodpadu', 'Naložení, odvoz a ekologická likvidace trávy, listí a větví', '/odvoz-bioodpadu'],
];

export const SCHEMAS = {
  localBusiness: {
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'LawnCareService'],
    '@id': `${SITE_URL}/#business`,
    name: SITE_NAME,
    image: `${SITE_URL}/og-image.jpg`,
    description: 'Profesionální sekání trávy, likvidace pozemků a zahradnické služby ve Dvoře Králové nad Labem. Bezplatná obhlídka, cena vždy předem.',
    url: SITE_URL,
    telephone: SITE_PHONE,
    email: 'info@seknuto.cz',
    // Volný text je pro Google i AI užitečnější než samotné „Kč“
    priceRange: 'Cena po bezplatné obhlídce, předem a bez skrytých poplatků',
    address: {
      '@type': 'PostalAddress',
      // Stejná adresa jako v patičce webu (sídlo podle IČO) – jednotné údaje
      // o firmě (NAP) pomáhají lokálnímu vyhledávání i AI odpovědím
      streetAddress: 'Fibichova 1294',
      addressLocality: 'Dvůr Králové nad Labem',
      addressRegion: 'Královéhradecký kraj',
      postalCode: '544 01',
      addressCountry: 'CZ',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 50.4318,
      longitude: 15.8142,
    },
    openingHoursSpecification: openingHoursSchema(),
    areaServed: servedArea(),
    slogan: 'Trávník bez starostí',
    founder: { '@type': 'Person', name: 'Dušan Macháček' },
    identifier: { '@type': 'PropertyValue', propertyID: 'IČO', value: '24889229' },
    paymentAccepted: 'Hotově, bankovním převodem',
    currenciesAccepted: 'CZK',
    knowsAbout: [
      'sekání trávy', 'údržba trávníku', 'stříhání tújí a živých plotů', 'kácení stromů',
      'rizikové kácení', 'likvidace zarostlých pozemků', 'úklid zahrady', 'vertikutace',
      'pokládka travního koberce', 'realizace zahrad', 'odvoz bioodpadu',
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Zahradnické služby',
      itemListElement: SERVICES.map(([name, description, path]) => ({
        '@type': 'Offer',
        description: 'Cena po bezplatné obhlídce, předem a bez skrytých poplatků',
        itemOffered: { '@type': 'Service', name, description, url: `${SITE_URL}${path}` },
      })),
    },
    sameAs: [
      'https://www.facebook.com/seknuto.cz',
      'https://www.instagram.com/seknuto.cz',
      'https://www.firmy.cz/detail/13988364-seknuto-cz-dvur-kralove-nad-labem.html',
    ],
  },

  faqPage: (items) => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  }),

  breadcrumb: (items) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map(({ name, url }, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name,
      item: `${SITE_URL}${url}`,
    })),
  }),

  service: ({ name, description, areaServed = null, url = null }) => ({
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: name,
    name,
    description,
    ...(url && { url: `${SITE_URL}${url}` }),
    // Odkaz na Organization z index.html – druhý LocalBusiness by vedle
    // hlavního bloku vypadal jako jiná firma (audit bod 14)
    provider: { '@type': 'Organization', '@id': `${SITE_URL}/#organization`, name: SITE_NAME },
    // Konkrétní město stránky na prvním místě, pak celá obsluhovaná oblast
    areaServed: areaServed && !SERVED_CITIES.includes(areaServed)
      ? [{ '@type': 'City', name: areaServed }, ...servedArea()]
      : servedArea(),
    offers: {
      '@type': 'Offer',
      description: 'Bezplatná obhlídka, cena předem a bez skrytých poplatků',
    },
  }),

  blogPost: ({ title, description, slug, datePublished, dateModified, image }) => ({
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    description,
    image: absoluteImage(image),
    url: `${SITE_URL}/blog/${slug}`,
    datePublished,
    dateModified: dateModified || datePublished,
    author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/blog/${slug}` },
  }),

  // Detail realizace v galerii – fotky + případná videa (jen veřejné http URL, ne base64)
  galleryProject: ({ title, description, slug, images = [], videos = [], uploadDate }) => {
    const httpImages = images.filter((u) => typeof u === 'string' && u.startsWith('http'));
    const httpVideos = videos.filter((u) => typeof u === 'string' && u.startsWith('http'));
    return {
      '@context': 'https://schema.org',
      '@type': 'ImageGallery',
      name: title,
      description: description || title,
      url: `${SITE_URL}/nase-prace/${slug}`,
      ...(httpImages.length > 0 && { image: httpImages }),
      ...(httpVideos.length > 0 && {
        video: httpVideos.map((v) => ({
          '@type': 'VideoObject',
          name: title,
          description: description || title,
          contentUrl: v,
          thumbnailUrl: httpImages[0] || DEFAULT_IMAGE,
          uploadDate: uploadDate || new Date().toISOString().slice(0, 10),
        })),
      }),
      publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    };
  },


  // Stránka města: stejná firma (stejné @id), ne „SeknuTo.cz – Trutnov" jako
  // další podnik na téže adrese. Město jen doplníme do obsluhované oblasti.
  localLanding: ({ cityName }) => {
    const base = SCHEMAS.localBusiness;
    const served = base.areaServed.some((a) => a.name === cityName)
      ? base.areaServed
      : [...base.areaServed, { '@type': 'City', name: cityName }];
    return { ...base, areaServed: served };
  },
};
